import { notification } from "antd";

const DEFAULT_PLACEMENT = "top";
const DEFAULT_DURATION = 0;

/**
 * Normalize notify args: (options) or (message) or (message, description) -> options object
 * @param {string|object} messageOrOptions - Message string or full options object
 * @param {string} [description] - Optional description when first arg is message
 * @returns {object} { message, description, placement, duration, ... }
 */
function normalizeOptions(messageOrOptions, description) {
  if (
    messageOrOptions != null &&
    typeof messageOrOptions === "object" &&
    !Array.isArray(messageOrOptions)
  ) {
    return {
      message: "",
      description: "",
      placement: DEFAULT_PLACEMENT,
      duration: DEFAULT_DURATION,
      ...messageOrOptions,
    };
  }
  return {
    message: messageOrOptions != null ? String(messageOrOptions) : "",
    description: description != null ? String(description) : "",
    placement: DEFAULT_PLACEMENT,
    duration: DEFAULT_DURATION,
  };
}

/**
 * Show a notification of the given type.
 * @param {string} type - "success" | "error" | "info" | "warning"
 * @param {string|object} messageOrOptions - Message string or options { message, description, placement, duration, pauseOnHover, showProgress }
 * @param {string} [description] - Description when messageOrOptions is a string
 */
function show(type, messageOrOptions, description) {
  const opts = normalizeOptions(messageOrOptions, description);
  notification[type]({
    message: opts.message,
    description: opts.description,
    placement: opts.placement ?? DEFAULT_PLACEMENT,
    duration: opts.duration ?? DEFAULT_DURATION,
    pauseOnHover: opts.pauseOnHover,
    showProgress: opts.showProgress,
  });
}

/**
 * Notify API – use notify.success(), notify.error(), notify.info(), notify.warning().
 * Accepts (options), (message), or (message, description).
 *
 * @example
 * notify.success("Saved successfully");
 * notify.error("Failed", "Please try again");
 * notify.info({ message: "Info", description: "Details", duration: 3 });
 * notify.warning({ message: "Warning", placement: "topRight" });
 */
export const notify = {
  success: (messageOrOptions, description) => show("success", messageOrOptions, description),
  error: (messageOrOptions, description) => show("error", messageOrOptions, description),
  info: (messageOrOptions, description) => show("info", messageOrOptions, description),
  warning: (messageOrOptions, description) => show("warning", messageOrOptions, description),
};
